import { AppForm, AppFormProps, AppTable, AppTableProps } from 'components/organisms';
import {
    FormNavButtons,
    SimpleInputElement,
    CurrencyInputElement,
    PriceDisplay,
    SimpleDatePicker,
} from 'components/molecules';
import { AxiosError } from 'axios';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { DietsFormFields, DietDTO, MealPriceWithDate } from 'types';
import { commonTexts, settingsTexts } from 'consts/text/index';
import { apiErrorCodes } from 'consts/api/apiErrorCodes';
import { useMealTypesPageNetworkManage } from 'pages/app/staff/settings/MealTypesPage/hooks/useMealTypesPageNetworkManage';
import { useMemo } from 'react';
import { parseStringIntoFormattedStringDate } from 'utils/parsers/dateTime/parseStringIntoFormattedStringDate';

export type DietAddEditFormProps = Omit<
    AppFormProps<DietsFormFields>,
    'width' | 'title' | 'name' | 'onFinish' | 'error' | 'withGoBack'
> & {
    onFinish: (dietData: DietsFormFields) => void;
    axiosError: AxiosError | null;
    isLoading: boolean;
    type: 'add' | 'edit';
    dietData?: DietDTO; // Optional diet data for edit mode to display meals table
};
export const DietAddEditForm = ({
    type,
    initialValues,
    onFinish,
    axiosError,
    dietData,
    ...props
}: DietAddEditFormProps) => {
    const isAddFlow = type === 'add';
    const title = isAddFlow ? settingsTexts.diets.addDiet : settingsTexts.diets.editDiet;
    const name = isAddFlow ? 'dietAdd' : 'dietEdit';
    const buttonText = isAddFlow ? commonTexts.actionLabels.add : commonTexts.actionLabels.save;
    const buttonIcon = isAddFlow ? undefined : <IconDeviceFloppy size={20} />;

    // Handle specific error codes
    const getDietAddEditError = () => {
        if (!axiosError) return '';

        const code = (axiosError.response?.data as { code: string })?.code;

        if (code === apiErrorCodes.DIET_MEAL_TYPE_REMOVE_ATTEMPT) {
            return settingsTexts.diets.errorsNotifications.dietMealTypeInUse;
        }
        if (code === apiErrorCodes.DIET_UPDATE_IN_PAST_ATTEMPT) {
            return settingsTexts.diets.errorsNotifications.dietUpdateInPast;
        }

        if (code === apiErrorCodes.DIET_NAME_TAKEN) {
            return settingsTexts.diets.errorsNotifications.dietNameTaken;
        }

        if (code === apiErrorCodes.DIET_NOT_FOUND) {
            return settingsTexts.diets.errorsNotifications.dietNotFound;
        }

        return commonTexts.errorMessages.unknown;
    };

    const dietAddEditError = getDietAddEditError();

    const { mealTypesData } = useMealTypesPageNetworkManage({
        onMealTypeRemoveRequestFinish: () => {},
    });

    // Generate dynamic meal price fields based on meal types
    const mealPriceFields = useMemo(
        () =>
            (mealTypesData ?? []).map((mealType) => (
                <CurrencyInputElement
                    key={mealType.id}
                    optional
                    label={mealType.name}
                    name={`mealPrice_${mealType.id}`}
                    placeholder={`Podaj cenę za ${mealType.name}`}
                />
            )),
        [mealTypesData],
    );

    // Generate columns for meals table (edit mode only)
    const mealsTableColumns: AppTableProps<MealPriceWithDate>['columns'] = useMemo(() => {
        if (!dietData || isAddFlow) return [];

        const baseColumns = [
            {
                title: 'Od',
                dataIndex: 'dateFrom',
                key: 'dateFrom',
                render: (dateFrom: string) => parseStringIntoFormattedStringDate(dateFrom),
                isVisibleAsExtendableInMobile: false,
            },
            {
                title: 'Do',
                dataIndex: 'dateTo',
                key: 'dateTo',
                render: (dateTo: string) =>
                    dateTo ? parseStringIntoFormattedStringDate(dateTo) : '-',
                isVisibleAsExtendableInMobile: false,
            },
        ];

        const priceColumns = (mealTypesData ?? []).map((mealType) => ({
            title: mealType.name,
            dataIndex: 'mealPrices',
            key: `mealPrice_${mealType.id}`,
            render: (mealPrices: MealPriceWithDate['mealPrices']) => {
                const mealPrice = mealPrices?.find((mp) => mp.mealTypeId === mealType.id);
                return <PriceDisplay value={mealPrice?.price ?? 0} />;
            },
            isVisibleAsExtendableInMobile: true,
        }));

        return [...baseColumns, ...priceColumns];
    }, [dietData, isAddFlow, mealTypesData]);

    return (
        <>
            <AppForm<DietsFormFields>
                title={title}
                name={name}
                onFinish={onFinish}
                error={dietAddEditError}
                withGoBack
                initialValues={initialValues}
                {...props}
            >
                <SimpleInputElement
                    label={settingsTexts.diets.columnNames.name}
                    name="name"
                    placeholder="Podaj nazwę diety"
                />
                <SimpleDatePicker
                    formItemLabel={commonTexts.dataLabels.pricesDateFrom}
                    name="dateFrom"
                    minDate={initialValues?.dateFrom?.startOf('month')}
                    placeholder="Wybierz datę od"
                />
                {mealPriceFields}

                <FormNavButtons
                    formValuesBeforeChanges={initialValues}
                    buttonText={buttonText}
                    leftIcon={buttonIcon}
                    withPlus={isAddFlow}
                />
            </AppForm>
            {!isAddFlow && dietData?.meals && dietData.meals.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                    <AppTable<MealPriceWithDate>
                        title="Historia cen posiłków"
                        columns={mealsTableColumns}
                        dataSource={dietData.meals}
                        isTableManageUtilsHidden
                        rowKey={(meal, index) => `${meal.dateFrom}-${meal.dateTo}-${index}`}
                        emptyInfo="Brak historii cen posiłków"
                        pagination={false}
                    />
                </div>
            )}
        </>
    );
};
